import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import classNames from "classnames";
import {
  fetchAiCalls,
  selectAiCalls,
  selectAiCallsLoading,
  selectHasMoreAiCalls,
  startQuery,
} from "../../../../store/slices/ai-calls";
import { selectCurrentProject } from "../../../../store/slices/projects";
import { selectUsers, fetchUsers } from "../../../../store/slices/user_management";
import AuditTrailHeader from "../audit-trail-header";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import { Body } from "../../../../components/main-body/body";
import "../../styles.css";
import "../../../../components/main-body/body.css";

// The tools an assistant can call, and what came of a call (lib/mcp/callLog.js).
const TOOLS = [
  "whoami", "search_content", "list_taxonomy", "find_similar_articles",
  "get_article", "get_fitfact", "get_task", "get_scenario", "get_plan",
].map((name) => ({ id: name, name }));

const OUTCOMES = [
  { id: "ok", name: "Answered" },
  { id: "not_found", name: "Not found" },
  { id: "invalid", name: "Refused: invalid request" },
  { id: "denied", name: "Refused: not allowed" },
  { id: "rate_limited", name: "Refused: limit reached" },
  { id: "timeout", name: "Timed out" },
  { id: "error", name: "Failed" },
];

const OUTCOME_NAMES = Object.fromEntries(OUTCOMES.map((outcome) => [outcome.id, outcome.name]));

// Arguments as the log keeps them: free text is { length, hash }, never the words.
const argsText = (args) => Object.entries(args || {})
  .map(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value) && "length" in value) {
      return `${key}: (text, ${value.length} chars)`;
    }
    return `${key}: ${Array.isArray(value) ? value.join(", ") : value}`;
  })
  .join("; ");

/**
 * LF360-2100 stage 1 — Analytics → Audit trail → AI calls: every call an AI
 * assistant made in this project, refusals included.
 *
 * Metadata only: who, which assistant, which tool, the outcome, how long, how
 * many records. The log has no titles or text, so this tab shows the ids of
 * what was read, never the content.
 */
const AiCallsTable = () => {
  const calls = useSelector(selectAiCalls);
  const hasMore = useSelector(selectHasMoreAiCalls);
  const loading = useSelector(selectAiCallsLoading);
  const usersFromStore = useSelector(selectUsers) || [];
  const projectId = useSelector(selectCurrentProject)?.id;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [filters, setFilters] = useState({});
  const nextPage = useRef(1);

  // The list is of one query: the open project and these filters.
  const key = JSON.stringify({ projectId, filters });

  useEffect(() => {
    if (!usersFromStore.length) {
      dispatch(fetchUsers());
    }
  }, [usersFromStore.length, dispatch]);

  const fetchPage = (page) => {
    nextPage.current = page + 1;
    dispatch(
      fetchAiCalls({
        page,
        userIds: filters.userIds,
        tools: filters.tools,
        outcomes: filters.outcomes,
        createdAtEnd: filters.period?.[filters.period.length - 1]?.end,
        createdAtStart: filters.period?.[filters.period.length - 1]?.start,
      })
    );
  };

  // A new project or new filters start a new list, whatever is still loading:
  // the slice drops pages asked for before this start, even when the same
  // project and filters come back.
  useEffect(() => {
    if (!projectId) return;
    dispatch(startQuery(key));
    fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // The next page of the same query, when the table scrolls to its end.
  const fetchData = () => {
    if (!loading) fetchPage(nextPage.current);
  };

  const columns = [
    {
      title: t("trails.table.time"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (_, call) => <span>{`${moment(call.createdAt).utc().format("DD MMM YYYY, HH:mm:ss")}, UTC`}</span>,
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (_, call) => <span>{call.user ? call.user.email : "Deleted user"}</span>,
    },
    {
      title: "Assistant",
      dataIndex: "client",
      key: "client",
      render: (_, call) => <span>{call.client.name ? `${call.client.name} (${call.client.host})` : call.client.host}</span>,
    },
    { title: "Tool", dataIndex: "tool", key: "tool" },
    {
      title: "Outcome",
      dataIndex: "outcome",
      key: "outcome",
      render: (_, call) => <span>{OUTCOME_NAMES[call.outcome] || call.outcome}</span>,
    },
    {
      title: "Details",
      dataIndex: "args",
      key: "args",
      render: (_, call) => (
        <span style={{ wordWrap: "break-word", wordBreak: "break-word" }}>
          {[
            argsText(call.args),
            call.resultCount !== null && call.resultCount !== undefined ? `${call.resultCount} records` : null,
            `${call.durationMs} ms`,
          ].filter(Boolean).join(" · ")}
        </span>
      ),
    },
  ];

  const filterItems = [
    { name: "Date peroid", id: "period", period: true },
    { name: "Users", id: "userIds", children: usersFromStore.map((u) => ({ id: u.id, name: u.fullName })) },
    { name: "Tool", id: "tools", children: TOOLS },
    { name: "Outcome", id: "outcomes", children: OUTCOMES },
  ];

  return (
    <Body disabledPaddings={true}>
      <AuditTrailHeader t={t} filterItems={filterItems} updateFilterValue={setFilters} />
      <div className={classNames("lf-content")}>
        <InfinityTable
          data={calls}
          fetchData={fetchData}
          hasMore={!!hasMore}
          tableColumns={columns}
          isLoading={loading}
        />
      </div>
    </Body>
  );
};

export default AiCallsTable;
